/**
 * Finds out that a newer build of the app is deployed while the user still runs an old one in a tab
 * that was never reloaded. Every build writes /version.json (see vite.config.js) with its build id; the
 * app compares it with the id it was built with. Checks run on a timer and whenever the tab comes back
 * to the foreground.
 */

export const CHECK_INTERVAL = 5 * 60 * 1000;
// a tab that is switched to again within this time doesn't check again
const MIN_GAP = 30 * 1000;

/** The build id the server serves now, or null if it can't be told (offline, no version.json, dev server). */
export async function fetchBuildId (baseUrl = "/") {
    try {
        const response = await fetch(baseUrl + "version.json?t=" + Date.now(), { cache: "no-store" });
        if (!response.ok) {
            return null;
        }
        const data = await response.json();
        return data && typeof data.buildId === "string" ? data.buildId : null;
    } catch (e) {
        return null;
    }
}

/**
 * Starts checking. [onNewVersion] is called once, when the server serves another build than
 * [currentBuildId]. Returns {stop, checkSoon}: [checkSoon] is for moments the user does something in the
 * app (navigating); it checks unless a check ran in the last 30 seconds.
 */
export function startVersionCheck ({ currentBuildId, onNewVersion, baseUrl = "/", interval = CHECK_INTERVAL, fetchId = fetchBuildId }) {
    let last = 0;
    let found = false;
    let timer = null;

    async function check () {
        if (found) {
            return;
        }
        last = Date.now();
        const id = await fetchId(baseUrl);
        if (id && id !== currentBuildId) {
            found = true;
            stop();
            onNewVersion(id);
        }
    }

    // checks unless one ran a moment ago
    function checkSoon () {
        if (Date.now() - last >= MIN_GAP) {
            check();
        }
    }

    function onVisible () {
        if (document.visibilityState === "visible") {
            checkSoon();
        }
    }

    function stop () {
        clearInterval(timer);
        document.removeEventListener("visibilitychange", onVisible);
    }

    timer = setInterval(check, interval);
    document.addEventListener("visibilitychange", onVisible);
    return { stop, checkSoon };
}
